"use client"

import { useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import { dashboardApi } from "@/lib/app-api"
import { useAuth } from "@/hooks/useAuth"

function localDayKey() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
}

const UNCLOCKED_REMINDER_COOLDOWN_MS = 2 * 60 * 60 * 1000

export function WorkAttentionMonitor() {
  const { user } = useAuth()
  const { data } = useQuery({
    queryKey: ["dashboard-attention", user?.id],
    queryFn: dashboardApi.getAttentionSummary,
    enabled: Boolean(user?.id),
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!data || !user?.id || typeof window === "undefined") return

    const dayKey = localDayKey()
    if (data.overdue.total > 0) {
      const overdueKey = `projectpulse:overdue-reminder:${user.id}:${dayKey}`
      if (!sessionStorage.getItem(overdueKey)) {
        sessionStorage.setItem(overdueKey, "1")
        toast.warning("Overdue work requires attention", {
          description: `${data.overdue.tasks} task(s) and ${data.overdue.bugs} bug(s) are overdue.`,
          action: {
            label: "Review",
            onClick: () => { window.location.href = "/dashboard" },
          },
        })
      }
    }

    if (data.time.today.should_prompt) {
      const unclockedKey = `projectpulse:unclocked-reminder:${user.id}:${dayKey}`
      const lastReminderAt = Number(sessionStorage.getItem(unclockedKey) || 0)
      if (!lastReminderAt || Date.now() - lastReminderAt >= UNCLOCKED_REMINDER_COOLDOWN_MS) {
        sessionStorage.setItem(unclockedKey, String(Date.now()))
        toast.warning("No backend timer is running", {
          description: `You have ${formatHours(data.time.today.unclocked_hours)} unclocked today.`,
          action: {
            label: "Start a timer",
            onClick: () => { window.location.href = "/tasks" },
          },
        })
      }
    }
  }, [data, user?.id])

  return null
}

function formatHours(value: number) {
  const totalMinutes = Math.max(0, Math.round(Number(value || 0) * 60))
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
}
